/**
 * Zone-specific ratio presets for multi-zone project estimation.
 * Each zone type has a default set of ratios. Users can override
 * individual ratios per zone via zoneOverrides.
 */

export type ZoneType = 'quartier' | 'mixte' | 'industriel';

export interface ZoneRatioSet {
  logements_per_ha: number;
  inhabitants_per_logement: number;
  emplois_per_logement_mixed: number;
  m2_activites_par_emploi: number;
  sbp_per_logement: number;
  pct_activites_sbp: number;
  pct_espaces_verts_surface: number;
  classes_per_logements_short: number;
  creche_places_per_logements: number;
  infra_per_logement: number;
  ecole_per_class: number;
  creche_per_place: number;
  co_per_logements: number;
  co_cost: number;
  sport_threshold: number;
  sport_cost: number;
  park_per_m2: number;
}

/** Quartier zone — residential-focused, uses FALLBACK_RATIOS baseline */
export const QUARTIER_RATIOS: ZoneRatioSet = {
  logements_per_ha: 65,
  inhabitants_per_logement: 2.3,
  emplois_per_logement_mixed: 0.80,
  m2_activites_par_emploi: 50,
  sbp_per_logement: 100,
  pct_activites_sbp: 0.12,
  pct_espaces_verts_surface: 0.22,
  classes_per_logements_short: 0.015,
  creche_places_per_logements: 0.025,
  infra_per_logement: 67000,
  ecole_per_class: 2060000,
  creche_per_place: 61000,
  co_per_logements: 0.0004,
  co_cost: 60000000,
  sport_threshold: 1200,
  sport_cost: 18000000,
  park_per_m2: 400,
};

/** Mixte zone — higher activity share, more employment per logement */
export const MIXTE_RATIOS: ZoneRatioSet = {
  ...QUARTIER_RATIOS,
  logements_per_ha: 45,
  emplois_per_logement_mixed: 1.2,
  pct_activites_sbp: 0.30,
};

/** Map zone type to default ratios */
export const ZONE_DEFAULTS: Record<'quartier' | 'mixte', ZoneRatioSet> = {
  quartier: QUARTIER_RATIOS,
  mixte: MIXTE_RATIOS,
};

/** User-friendly labels for ratio keys */
export const RATIO_LABELS: Record<string, string> = {
  logements_per_ha: 'Logements / ha',
  inhabitants_per_logement: 'Habitants / logement',
  emplois_per_logement_mixed: 'Emplois / logement',
  sbp_per_logement: 'SBP / logement (m²)',
  pct_activites_sbp: 'Part activités SBP',
  pct_espaces_verts_surface: 'Part espaces verts',
  classes_per_logements_short: 'Classes / logement',
  creche_places_per_logements: 'Places crèche / logement',
  infra_per_logement: 'Infra CHF / logement',
  ecole_per_class: 'École CHF / classe',
  creche_per_place: 'Crèche CHF / place',
  co_per_logements: 'CO / logement',
  co_cost: 'CO CHF / unité',
  sport_threshold: 'Seuil sport (logements)',
  sport_cost: 'Sport CHF / unité',
  park_per_m2: 'Parc CHF / m²',
};

/**
 * Resolve effective ratios for a zone, applying user overrides on top of defaults.
 */
export function resolveZoneRatios(
  zone: 'quartier' | 'mixte',
  overrides?: Record<string, number>,
): ZoneRatioSet {
  const base = ZONE_DEFAULTS[zone];
  if (!overrides) return base;
  return { ...base, ...overrides } as ZoneRatioSet;
}
